import React, { useRef, useEffect, useState } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import './Laptop.css'
import { LaptopProps, HotspotItemProps } from './Hotspot.d'
import FloatingText from './FloatingText'
import RichText from '@/utils/RichText'
import { Button } from '@/components/ui/button'
import useResponsive from '@/hooks/useResponsive'

const Laptop = ({
  label,
  heading,
  content,
  cta_text,
  cta_link,
  width = 800,
  className = '',
  image,
  hotspots,
  onRegionClick = region => console.log(`Clicked on region: ${region}`),
}: LaptopProps) => {
  const indicatorSize = 48
  const hotspotRadius = 20
  const containerRef = useRef<HTMLDivElement>(null)
  const laptopRef = useRef<HTMLDivElement>(null)
  const screenRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const [imgLoaded, setImgLoaded] = useState(true)
  const [scale, setScale] = useState(1)
  const [activeHotspot, setActiveHotspot] = useState<string | null>(null)
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 })
  const { isMobile } = useResponsive()

  // @INFO: Track scroll progress within the container
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: isMobile ? ['start end', 'end start'] : ['start start', 'end start'],
  })

  const topRotationX = useTransform(scrollYProgress, [0, 0.3, 1], [-90, 0, 0])
  const stickyOpacity = useTransform(
    scrollYProgress,
    [0, 0.1, 1, 1],
    [1, 1, 1, 0],
    // [0, 0.1, 0.9, 1],
    // [1, 1, 1, 0],
  )
  const containerHeight = `${width * 2.3}px`

  // @INFO: Close tooltips when laptop goes out of view (scrolled too far)
  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', _ => {
      // @INFO: Disabled condition, it should automatically close the tooltip when the laptop goes out of view
      // if (value > 0.9 || value < 0.1) {
      setActiveHotspot(null)
      // }
    })

    return () => unsubscribe()
  }, [scrollYProgress])

  const handleHotspotClick = (
    e: React.MouseEvent<HTMLDivElement>,
    hotspotId: string,
  ) => {
    e.preventDefault()
    onRegionClick(hotspotId)
    setActiveHotspot(prevState => (prevState === hotspotId ? null : hotspotId))
  }

  const handleImageLoad = () => {
    if (imgRef.current) {
      setImgLoaded(true)
      updateScale()
    }
  }

  const updateViewportSize = () => {
    setViewportSize({
      width: window.innerWidth,
      height: window.innerHeight,
    })
  }

  // @INFO: Calculate and update scale based on current screen width vs base width
  const updateScale = () => {
    if (!screenRef.current || !imgRef.current) {
      requestAnimationFrame(updateScale)
      return
    }

    const currentWidth = screenRef.current.clientWidth
    if (currentWidth === 0) {
      // If width is 0, element might not be fully rendered yet
      requestAnimationFrame(updateScale)
      return
    }

    const newScale = currentWidth / 1300
    setScale(newScale)
  }

  useEffect(() => {
    let mounted = true

    const initializeRefs = () => {
      if (!mounted) return

      const mockupElement = document.querySelector('.mockup')
      if (mockupElement) {
        mockupElement.classList.add('loaded')
      }

      if (!screenRef.current) {
        // If screenRef is not ready, try again in the next frame
        requestAnimationFrame(initializeRefs)
        return
      }

      updateScale()
      updateViewportSize()
    }

    // Start initialization
    initializeRefs()

    const handleResize = () => {
      if (!mounted) return
      updateScale()
      updateViewportSize()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      mounted = false
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // @INFO: Get the position of a hotspot in the viewport, adjusting for responsive scaling
  const getHotspotPosition = (hotspot: HotspotItemProps) => {
    // Add retry mechanism if screenRef is not ready
    let attempts = 0
    const maxAttempts = 5

    const tryGetPosition = () => {
      if (!screenRef.current && attempts < maxAttempts) {
        attempts++
        // Try again in 100ms
        setTimeout(tryGetPosition, 100)
        return null
      }

      if (!screenRef.current || !hotspot) {
        console.warn('Screen ref or hotspot not available after retries')
        return null
      }

      const screenRect = screenRef.current.getBoundingClientRect()

      // Calculate the absolute position within the viewport
      const x = screenRect.left + Number(hotspot.x_coordinate) * scale
      const y = screenRect.top + Number(hotspot.y_coordinate) * scale

      return { x, y }
    }

    return tryGetPosition()
  }

  // @INFO: Get laptop bottom position for mobile tooltips
  const getLaptopBottomPosition = () => {
    if (!laptopRef.current || typeof window === 'undefined') return null

    const laptopRect = laptopRef.current.getBoundingClientRect()
    return {
      x: laptopRect.left + laptopRect.width / 2,
      y: laptopRect.bottom + 20, // 20px below laptop
    }
  }

  return (
    <div className="relative flex justify-center" data-testdata="hotspot-laptop">
      <div
        ref={containerRef}
        className="relative w-full p-10"
        style={{ height: containerHeight }}
      >
        <motion.div
          ref={laptopRef}
          className={`sticky top-1/2 transform -translate-y-1/2 flex justify-center items-center ${className}`}
          style={{
            opacity: stickyOpacity,
            pointerEvents: 'auto',
          }}
        >
          <div
            className="container"
            style={{
              width: `${width}px`,
              maxWidth: '100%',
              position: 'relative',
            }}
          >
            <div
              className={`flex flex-col justify-center items-center w-full max-w-6xl  px-6 py-8`}
            >
              <div
                className={`prose font-sans uppercase text-sm  tracking-wide font-semibold mb-4`}
              >
                <RichText>{label}</RichText>
              </div>

              {/* Title */}
              <div className="prose text-5xl font-bold leading-tight mb-4 max-w-2xl">
                <RichText>{heading}</RichText>
              </div>

              {/* Content */}
              <div className="prose mt-2 max-w-2xl">
                <RichText>{content}</RichText>
              </div>
            </div>
            <div className="mockup mockup-macbook">
              <motion.div
                className="part top"
                style={{
                  rotateX: topRotationX,
                  transformOrigin: '50% 100%',
                }}
              >
                <img
                  src="https://d1xm195wioio0k.cloudfront.net/images/mockup/macbook-top.svg"
                  alt="MacBook top"
                  className="top w-full"
                />
                <img
                  src="https://d1xm195wioio0k.cloudfront.net/images/mockup/macbook-cover.svg"
                  alt="MacBook cover"
                  className="cover w-full"
                />
                <motion.div
                  ref={screenRef}
                  className="screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-purple-900"
                >
                  <div className="relative w-full h-full overflow-hidden">
                    <img
                      ref={imgRef}
                      src={image}
                      className="z-1 absolute object-cover w-full h-full image-rendering-crisp"
                      onLoad={handleImageLoad}
                      alt="Dashboard interface"
                    />

                    {/* @INFO: Clickable hotspot areas */}
                    {imgLoaded &&
                      hotspots.map((hotspot, idx) => (
                        <div
                          key={idx}
                          data-hotspot-id={hotspot._uid}
                          className="absolute cursor-pointer z-20"
                          style={{
                            left: `${Number(hotspot.x_coordinate) * scale}px`,
                            top: `${Number(hotspot.y_coordinate) * scale}px`,
                            width: `${hotspotRadius * 2 * scale}px`,
                            height: `${hotspotRadius * 2 * scale}px`,
                            transform: 'translate(-50%, -50%)',
                          }}
                          onClick={e => handleHotspotClick(e, hotspot._uid)}
                          //   title={hotspot.title}
                          title="Test"
                        />
                      ))}

                    {/* @INFO: Visual indicators for clickable areas */}
                    {imgLoaded &&
                      hotspots.map(hotspot => (
                        <div
                          key={`indicator-${hotspot.id}`}
                          className="z-30 pointer-events-none absolute"
                          style={{
                            left: `${Number(hotspot.x_coordinate) * scale}px`,
                            top: `${Number(hotspot.y_coordinate) * scale}px`,
                            transform: 'translate(-50%, -50%)',
                          }}
                        >
                          <div
                            className="animate-pulse rounded-full bg-[#00D2A0] shadow-lg shadow-green-300/50"
                            style={{
                              width: `${indicatorSize * scale}px`,
                              height: `${indicatorSize * scale}px`,
                            }}
                          />
                          {/* Dotted circle around hotspot indicator */}
                          <div
                            className="absolute top-0 left-0 rounded-full"
                            style={{
                              width: `${60 * scale}px`,
                              height: `${60 * scale}px`,
                            }}
                          />
                        </div>
                      ))}
                  </div>
                </motion.div>
              </motion.div>

              <div className="part bottom">
                <img
                  src="https://d1xm195wioio0k.cloudfront.net/images/mockup/macbook-cover.svg"
                  alt="MacBook cover"
                  className="cover w-full"
                />
                <img
                  src="https://d1xm195wioio0k.cloudfront.net/images/mockup/macbook-bottom.svg"
                  alt="MacBook bottom"
                  className="bottom w-full"
                />
              </div>
            </div>
            {cta_text && (
              <div className="mt-8">
                <Button to={cta_link} variant="secondary_green">
                  {cta_text}
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* @INFO: Overlay for floating text */}
      {activeHotspot && (
        <FloatingText
          indicatorSize={indicatorSize}
          hotspot={hotspots.find((h: HotspotItemProps) => h._uid === activeHotspot)!}
          getPosition={getHotspotPosition}
          getLaptopBottom={getLaptopBottomPosition}
          viewportSize={viewportSize}
        />
      )}
    </div>
  )
}

export default Laptop
